/**
 * What the mini app reports about a person's actions (see hooks/useUserEvents). Only names from the code: a screen is
 * its path with record ids masked, a button is its data-testid or its position. Never visible text, because a button
 * can show a person's name, a city or an answer. The server accepts the same character set (api/routes/me-events.ts).
 */
import { maskLinkParam, maskRecordIds } from '@wds/astro-bot-shared/marketing/tag';

const MAX_LEN = 60;
const OUTSIDE_ALLOWED = /[^A-Za-z0-9_./:>#-]/g;

/** Masks anything that names a record, and keeps the token inside the characters the server accepts. */
export function maskToken(s: string): string {
  return maskRecordIds(s).replace(OUTSIDE_ALLOWED, '_').slice(0, MAX_LEN);
}

/** The startapp parameter: a marketing tag survives whole, record ids and @usernames do not. */
export function maskStartParam(param: string): string {
  return maskLinkParam(param).replace(OUTSIDE_ALLOWED, '_').slice(0, MAX_LEN);
}

const CLICKABLE = 'button, a, summary, select, label, input[type="checkbox"], input[type="radio"], [role="button"], [role="tab"], [role="link"]';

/** A pressed control's name, or null when the press did not land on a control. */
export function clickedControl(target: EventTarget | null): string | null {
  if (!(target instanceof Element)) return null;
  const control = target.closest(CLICKABLE);
  if (!control) return null;
  const own = control.getAttribute('data-testid');
  if (own) return maskToken(own);
  const scope = control.parentElement?.closest('[data-testid]') ?? null;
  const among = Array.from((scope ?? document).querySelectorAll(CLICKABLE));
  const position = `${control.tagName.toLowerCase()}#${among.indexOf(control) + 1}`;
  const name = scope?.getAttribute('data-testid');
  return maskToken(name ? `${name}>${position}` : `page>${position}`);
}

const MARKS = [25, 50, 75, 100] as const;
export type ScrollMark = (typeof MARKS)[number];

/** Marks newly reached on a vertical scroller; a short strip or a page that barely scrolls reports nothing. */
export function reachedMarks(
  m: { scrollTop: number; clientHeight: number; scrollHeight: number },
  sent: ReadonlySet<number>,
): ScrollMark[] {
  if (m.clientHeight < 200 || m.scrollHeight - m.clientHeight < 50) return [];
  const percent = ((m.scrollTop + m.clientHeight) / m.scrollHeight) * 100;
  return MARKS.filter((mark) => percent + 0.5 >= mark && !sent.has(mark));
}
